"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { StandardCurvePlot } from "@/components/tools/biology-lab/standard-curve-plot";
import type { StandardCurveCalculatorStrings } from "@/components/tools/standard-curve-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  fixed,
  type NumberFormatter,
  plain,
  sig,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import {
  backCalculate,
  type Fit,
  type FitError,
  type Point,
  readSamples,
  type SampleResult,
  type SampleStatus,
} from "@/lib/tool-kit/biology-lab/standard-curve";

type Strings = StandardCurveCalculatorStrings;

export function fitErrorText(code: FitError, strings: Strings): string {
  const messages: Record<FitError, string> = {
    points: strings.errPoints,
    distinct: strings.errDistinct,
    flat: strings.errFlat,
    "fourpl-points": strings.errFourPlPoints,
    "fourpl-failed": strings.errFourPlFailed,
  };
  return messages[code];
}

function statusText(status: SampleStatus, strings: Strings): string {
  const messages: Record<SampleStatus, string> = {
    within: strings.statusWithin,
    above: strings.statusAbove,
    below: strings.statusBelow,
    unreachable: strings.statusUnreachable,
  };
  return messages[status];
}

function equation(fit: Fit, strings: Strings, fmt: NumberFormatter): string {
  if (fit.kind === "linear") {
    const sign = fit.intercept < 0 ? "−" : "+";
    return t(strings.equationLinear, {
      slope: sig(fmt, fit.slope, 4),
      sign,
      intercept: sig(fmt, Math.abs(fit.intercept), 4),
    });
  }
  return t(strings.equation4pl, {
    a: sig(fmt, fit.a, 4),
    d: sig(fmt, fit.d, 4),
    c: sig(fmt, fit.c, 4),
    b: sig(fmt, fit.b, 4),
  });
}

function FitSummary({
  fit,
  strings,
  fmt,
}: {
  fit: Fit;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  return (
    <>
      <div className="rounded-lg border border-primary/30 bg-card p-4 text-center">
        <p className="text-muted-foreground text-sm">
          {fit.kind === "linear" ? strings.fitLinearName : strings.fit4plName}
        </p>
        <p className="break-words font-mono font-semibold text-foreground text-lg">
          {equation(fit, strings, fmt)}
        </p>
      </div>
      <StatGrid>
        <StatCard emphasis label={strings.r2} value={fixed(fmt, fit.r2, 4)} />
        <StatCard label={strings.points} value={plain(fmt, fit.n)} />
        {Number.isFinite(fit.rmse) ? (
          <StatCard label={strings.rmse} value={sig(fmt, fit.rmse, 3)} />
        ) : null}
        {fit.kind === "4pl" ? (
          <>
            <StatCard label={strings.paramA} value={sig(fmt, fit.a, 4)} />
            <StatCard label={strings.paramD} value={sig(fmt, fit.d, 4)} />
            <StatCard label={strings.paramC} value={sig(fmt, fit.c, 4)} />
            <StatCard label={strings.paramB} value={sig(fmt, fit.b, 4)} />
          </>
        ) : null}
      </StatGrid>
    </>
  );
}

function SampleTable({
  results,
  unit,
  strings,
  fmt,
}: {
  results: SampleResult[];
  unit: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const head = [
    strings.colSample,
    strings.colReading,
    `${strings.colConc}${unit === "" ? "" : ` (${unit})`}`,
    `${strings.colOriginal}${unit === "" ? "" : ` (${unit})`}`,
    strings.colStatus,
  ];
  const rows = results.map((r, index) => [
    r.label ?? t(strings.unnamed, { n: plain(fmt, index + 1) }),
    plain(fmt, r.reading),
    r.concentration === null ? strings.none : sig(fmt, r.concentration, 4),
    r.original === null ? strings.none : sig(fmt, r.original, 4),
    statusText(r.status, strings),
  ]);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.samplesHeading}</h3>
      <DataTable head={head} rows={rows} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyTable}
        small
        text={toTsv(head, rows)}
      />
    </div>
  );
}

function BackTable({
  fit,
  points,
  strings,
  fmt,
}: {
  fit: Fit;
  points: readonly Point[];
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const head = [
    strings.colNominal,
    strings.colStdReading,
    strings.colBack,
    strings.colRecovery,
  ];
  const rows = backCalculate(fit, points).map((b) => [
    plain(fmt, b.x),
    plain(fmt, b.y),
    b.back === null || b.x === 0 ? strings.none : sig(fmt, b.back, 4),
    b.recovery === null ? strings.none : `${fixed(fmt, b.recovery, 1)}%`,
  ]);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.backHeading}</h3>
      <DataTable head={head} rows={rows} />
      <p className="text-muted-foreground text-xs">{strings.backNote}</p>
    </div>
  );
}

/** The fit, the plot and the unknowns read from it. */
export function CurveResults({
  fit,
  points,
  samples,
  dilution,
  unit,
  responseLabel,
  logX,
  strings,
  fmt,
}: {
  fit: Fit;
  points: readonly Point[];
  samples: readonly { label: string | null; value: number }[];
  dilution: number;
  unit: string;
  responseLabel: string;
  logX: boolean;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const results = readSamples(fit, points, samples, dilution);
  const plotted = results
    .filter(
      (r): r is SampleResult & { concentration: number } =>
        r.concentration !== null && r.concentration >= 0
    )
    .map((r) => ({ concentration: r.concentration, reading: r.reading }));
  const lowPoints = fit.kind === "4pl" && points.length === 4;
  return (
    <>
      <FitSummary fit={fit} fmt={fmt} strings={strings} />
      {lowPoints ? <Notice>{strings.fourPointsNote}</Notice> : null}
      <StandardCurvePlot
        fit={fit}
        fmt={fmt}
        label={t(strings.plotLabel, { n: plain(fmt, points.length) })}
        logX={logX}
        points={points}
        samples={plotted}
        xLabel={
          unit === ""
            ? strings.axisConcentration
            : `${strings.axisConcentration} (${unit})`
        }
        yLabel={responseLabel}
      />
      {results.length > 0 ? (
        <SampleTable
          fmt={fmt}
          results={results}
          strings={strings}
          unit={unit}
        />
      ) : (
        <Notice>{strings.noSamples}</Notice>
      )}
      <BackTable fit={fit} fmt={fmt} points={points} strings={strings} />
    </>
  );
}

export function FitFailure({
  code,
  strings,
}: {
  code: FitError;
  strings: Strings;
}) {
  return <ErrorNotice>{fitErrorText(code, strings)}</ErrorNotice>;
}
